import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Gauge,
  Rocket,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  RotateCcw,
  Trophy,
} from 'lucide-react';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import '../../styles/module-shared.css';
import './account-tier.css';

const THEME = getThemeColor('primary');

const STEPS = [
  {
    title: 'Earn Points',
    eyebrow: 'Step 01',
    body: 'Collect level points from active depositing clients and their trading activity in a rolling evaluation window (usually 30 days).',
    tip: 'More qualified activity = faster climb',
    icon: TrendingUp,
    tone: 'rose',
  },
  {
    title: 'Upgrade Tier',
    eyebrow: 'Step 02',
    body: 'Hit the next tier’s point threshold and your account upgrades automatically — typically the next day — with a higher per-lot rate.',
    tip: 'Upgrades unlock better commission',
    icon: Rocket,
    tone: 'amber',
  },
  {
    title: 'Maintain Status',
    eyebrow: 'Step 03',
    body: 'Keep enough activity in the maintenance window to hold your tier. Fall short and you may drop when the period resets.',
    tip: 'Consistency protects your tier',
    icon: ShieldCheck,
    tone: 'slate',
  },
];

const EXAMPLE_FLOW = [
  {
    label: 'New 30-day period starts',
    detail: 'Points restart at 0 for the new window while your current tier benefits stay active.',
    icon: Gauge,
    tone: 'neutral',
  },
  {
    label: 'Reach the next tier (e.g. 200 pts)',
    detail: 'Clear the threshold and your per-lot rate upgrades for the rest of the period.',
    icon: Trophy,
    tone: 'success',
  },
  {
    label: 'Keep climbing in the same window',
    detail: 'Extra points can unlock another tier before the evaluation period ends.',
    icon: Sparkles,
    tone: 'success',
  },
  {
    label: 'Miss the next target',
    detail: 'Finish short of the goal and that window’s leftover progress does not carry over.',
    icon: RotateCcw,
    tone: 'warn',
  },
  {
    label: 'Period ends — points reset',
    detail: 'A fresh evaluation begins. Your current tier may still apply during maintenance.',
    icon: Gauge,
    tone: 'neutral',
  },
  {
    label: 'Tier extended',
    detail: 'Meet maintenance requirements and your tier benefits roll into the next review window.',
    icon: ShieldCheck,
    tone: 'success',
  },
];

const AccountTierHowItWorks = () => (
  <div className="page at-page">
    <div className="page-inner">
      <ModulePageHeader
        icon={BookOpen}
        title="How It Works"
        subtitle="A clear path from earning points to keeping your tier"
        iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
      />

      <section className="at-how-hero">
        <div className="at-how-hero__copy">
          <span className="at-how-hero__eyebrow">Account Tier Playbook</span>
          <h2>Earn. Upgrade. Hold the line.</h2>
          <p>
            Three simple moves decide your commission power — track them, climb faster, and protect
            every tier you unlock.
          </p>
        </div>
        <div className="at-how-hero__stat">
          <strong>3</strong>
          <span>core steps</span>
        </div>
      </section>

      <section className="at-how-grid">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          return (
            <article key={step.title} className={`at-how-card at-how-card--${step.tone}`}>
              <div className="at-how-card__top">
                <span className="at-how-card__eyebrow">{step.eyebrow}</span>
                <span className="at-how-card__icon">
                  <Icon className="w-5 h-5" />
                </span>
              </div>
              <div className="at-how-card__num">{index + 1}</div>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
              <div className="at-how-card__tip">{step.tip}</div>
            </article>
          );
        })}
      </section>

      <section className="at-card at-example-panel at-example-panel--rich">
        <div className="at-example-head">
          <div>
            <span className="at-how-hero__eyebrow">Story mode</span>
            <h2 className="at-how-heading" style={{ marginBottom: '0.35rem' }}>
              Example journey
            </h2>
            <p className="at-example-intro" style={{ margin: 0 }}>
              One evaluation period — upgrades, a shortfall, then a reset and extension.
            </p>
          </div>
        </div>

        <div className="at-story">
          {EXAMPLE_FLOW.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className={`at-story__row at-story__row--${item.tone}`}>
                <div className="at-story__rail">
                  <span className="at-story__node">
                    <Icon className="w-4 h-4" />
                  </span>
                  {index < EXAMPLE_FLOW.length - 1 ? <span className="at-story__line" /> : null}
                </div>
                <div className="at-story__card">
                  <div className="at-story__label">{item.label}</div>
                  <p>{item.detail}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="at-link-row">
          <Link className="at-btn at-btn--primary" to="/account-tier/progress">
            View My Progress <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link className="at-btn at-btn--ghost" to="/account-tier/instruments-commission">
            Instruments Commission
          </Link>
        </div>
      </section>
    </div>
  </div>
);

export default AccountTierHowItWorks;
